import { useState, useEffect, useCallback } from "react";
import { startRegistration, startAuthentication, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import {
  webauthnRegisterOptions,
  webauthnRegisterVerify,
  webauthnLoginOptions,
  webauthnLoginVerify,
} from "../api/webauthn";

/**
 * Hook para gestionar autenticación biométrica (WebAuthn).
 * Proporciona funciones para registrar credenciales y hacer login biométrico.
 */
export function useBiometricAuth() {
  const [isSupported, setIsSupported] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setIsSupported(browserSupportsWebAuthn());
  }, []);

  /**
   * Registra una nueva credencial biométrica para el usuario autenticado.
   * @param {string} token - JWT del usuario
   * @returns {Promise<{success: boolean, message?: string}>}
   */
  const register = useCallback(async (token) => {
    setLoading(true);
    setError(null);
    try {
      // 1. Obtener opciones del servidor
      const options = await webauthnRegisterOptions(token);

      // 2. Activar prompt biométrico del navegador
      const credential = await startRegistration({ optionsJSON: options });

      // 3. Enviar respuesta al servidor para verificar y guardar
      const result = await webauthnRegisterVerify(token, credential);

      setLoading(false);
      return { success: true, message: result.message };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Error al registrar biometría";
      setError(msg);
      setLoading(false);
      return { success: false, message: msg };
    }
  }, []);

  /**
   * Realiza login biométrico.
   * @param {string} email - Email del usuario
   * @returns {Promise<{success: boolean, data?: object, message?: string}>}
   */
  const authenticate = useCallback(async (email) => {
    setLoading(true);
    setError(null);
    try {
      // 1. Obtener opciones de autenticación
      const options = await webauthnLoginOptions(email);
      const { userId, ...authOptions } = options;

      // 2. Activar prompt biométrico
      const credential = await startAuthentication({ optionsJSON: authOptions });

      // 3. Verificar en servidor y obtener tokens
      const data = await webauthnLoginVerify(userId, credential);

      setLoading(false);
      return { success: true, data };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Error en autenticación biométrica";
      setError(msg);
      setLoading(false);
      return { success: false, message: msg };
    }
  }, []);

  /**
   * Verifica si el email tiene credenciales biométricas almacenadas en localStorage.
   */
  const hasBiometricForEmail = useCallback((email) => {
    if (!email) return false;
    try {
      const stored = localStorage.getItem("biometric_email");
      return stored === email.trim().toLowerCase();
    } catch {
      return false;
    }
  }, []);

  /**
   * Guarda en localStorage que este email tiene biometría registrada.
   */
  const saveBiometricEmail = useCallback((email) => {
    localStorage.setItem("biometric_email", email.trim().toLowerCase());
  }, []);

  /**
   * Elimina el marca de biometría del localStorage.
   */
  const clearBiometricEmail = useCallback(() => {
    localStorage.removeItem("biometric_email");
  }, []);

  return {
    isSupported,
    loading,
    error,
    register,
    authenticate,
    hasBiometricForEmail,
    saveBiometricEmail,
    clearBiometricEmail,
  };
}
